import { Badge, CircularProgress } from '@components'
import { getExpenseClaimPageUrl } from '@constants'
import { ExpenseClaim } from '@query'
import { useVirtualList } from '@hooks'
import { formatDate } from '@utils'
import { formatEurosWithOpts } from '@utils/formatCurrency'
import React from 'react'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { expenseClaimListMeta, expenseClaimListTitle, expenseClaimStage } from './claimListDisplay'
import { STATUS_BADGE, claimantName } from './shared'

const CLAIM_ROW_HEIGHT = 76

type ClaimListProps = {
  slug: string
  claims: ExpenseClaim[]
  currentUserId: number | undefined
  markOwnAsAwaitingYou: boolean
  isFetchingNextPage: boolean
  onScrollBottom: () => void
  t: TFunction
}

export const ClaimList: React.FC<ClaimListProps> = ({
  slug,
  claims,
  currentUserId,
  markOwnAsAwaitingYou,
  isFetchingNextPage,
  onScrollBottom,
  t
}) => {
  const isAwaitingMe = (claim: ExpenseClaim) =>
    claim.status === 'AWAITING_APPROVAL' &&
    (claim.claimant.id !== currentUserId || markOwnAsAwaitingYou)

  const handleScrollBottom = () => {
    if (isFetchingNextPage) return
    onScrollBottom()
  }

  const { scrollRef, renderItems } = useVirtualList({
    items: claims,
    itemSize: CLAIM_ROW_HEIGHT,
    onScrollBottom: handleScrollBottom
  })

  return (
    <ClaimTableBody ref={scrollRef}>
      {renderItems((claim, style) => {
        const listMeta = expenseClaimListMeta(claim, t)
        return (
          <ClaimRowWrapper key={claim.id} style={style}>
            <ClaimRow to={getExpenseClaimPageUrl(slug, claim.id)}>
              <div>
                <ClaimTitle>{expenseClaimListTitle(claim, t)}</ClaimTitle>
                {listMeta ? <ClaimMeta>{listMeta}</ClaimMeta> : null}
              </div>
              <span>{claim.submitted_by_name || claimantName(claim)}</span>
              <span>{formatDate(claim.submitted_at ?? claim.created_at)}</span>
              <Muted>{expenseClaimStage(claim, t)}</Muted>
              <Amount>{formatEurosWithOpts(claim.total_amount)}</Amount>
              <span className="end">
                {isAwaitingMe(claim) ? (
                  <Badge type="warning">{t('travelAndExpenses.list.filter.awaiting_you')}</Badge>
                ) : (
                  <Badge type={STATUS_BADGE[claim.status]}>
                    {t(`travelAndExpenses.status.${claim.status}`)}
                  </Badge>
                )}
              </span>
            </ClaimRow>
          </ClaimRowWrapper>
        )
      })}
      {isFetchingNextPage && <ClaimListLoadingRow t={t} />}
    </ClaimTableBody>
  )
}

const claimGrid = css`
  display: grid;
  grid-template-columns:
    minmax(200px, 2fr) minmax(140px, 1fr) minmax(110px, 1fr) minmax(140px, 1fr)
    120px 150px;
  gap: ${({ theme }) => theme.spacing.md}rem;
  align-items: center;
  min-width: ${({ theme }) => theme.layout.claimListScrollMin}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;

  .end {
    justify-self: end;
  }
`

export const ClaimTableShell = styled.div`
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow-x: auto;
  overflow-y: hidden;
  flex: 1;
  min-width: 0;
  min-height: 0;
  max-height: 100%;
  display: flex;
  flex-direction: column;
`

export const ClaimHeader = styled.div`
  ${claimGrid}
  flex-shrink: 0;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
  padding-bottom: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.capsTight};
  color: ${({ theme }) => theme.colors.iconGray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const ClaimTableBody = styled.div`
  overflow-x: hidden;
  overflow-y: auto;
  flex: 1;
  min-width: ${({ theme }) => theme.layout.claimListScrollMin}rem;
  min-height: 0;
`

const ClaimRowWrapper = styled.div`
  box-sizing: border-box;
`

const ClaimRow = styled(Link)`
  ${claimGrid}
  display: grid;
  height: 100%;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.neutralBlack} !important;
  text-decoration: none !important;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const ClaimTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const ClaimMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxxs}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.iconGray};
`

const Amount = styled.span`
  justify-self: end;
  font-variant-numeric: tabular-nums;
`

const LoadingMoreRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

export const ClaimListLoadingRow: React.FC<{ t: TFunction }> = ({ t }) => (
  <LoadingMoreRow aria-busy="true">
    <CircularProgress
      isLoading
      size={22}
      progress={0}
      aria-label={t('travelAndExpenses.list.loadingMore')}
    />
    <span>{t('travelAndExpenses.list.loadingMore')}</span>
  </LoadingMoreRow>
)

export const EmptyRow = styled.div`
  padding: ${({ theme }) => theme.spacing.xl}rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.iconGray};
`
